import { Link } from 'react-router-dom';
import { Button } from '@usacow/ds';

const MODULES = [
  ['Statement deconstructor', 'Tag the statement by role and commit to a formal model.'],
  [
    'Constraint sniffer',
    'Turn N, Q, K, and limits into a complexity budget that rules out approaches.',
  ],
  [
    'Pattern discriminator',
    'Tell apart problems that look the same but need different algorithms.',
  ],
  ['State visualizer', 'Predict the next step of BFS, Dijkstra, DP, segment trees, and more.'],
  ['Invariant builder', 'Pick or complete the invariant that proves an algorithm correct.'],
  ['Counterexample trainer', 'Break a wrong greedy with the smallest failing input.'],
  ['Implementation bridge', 'Cross from a correct idea to correct code without the usual bugs.'],
  ['Contest simulation', 'Train under time, then get a postmortem of where your reasoning went.'],
];

export function HowItWorks() {
  return (
    <div className="mkt-page">
      <div className="t-eyebrow">how it works</div>
      <h1 className="mkt-page__h">A reasoning gym, not a problem archive.</h1>
      <p className="mkt-page__lead">
        usaCow runs you through short, active drills and full guided problems. Every answer is
        graded deterministically and mapped to a specific reasoning skill, so you always know what
        to fix next.
      </p>
      <div className="mkt-modlist">
        {MODULES.map(([t, b]) => (
          <div className="mkt-mod" key={t}>
            <div className="mkt-mod__t">{t}</div>
            <div className="mkt-mod__b">{b}</div>
          </div>
        ))}
      </div>
      <div className="mkt-page__cta">
        <Link to="/auth">
          <Button variant="primary" size="lg">
            Start the diagnostic
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function About() {
  return (
    <div className="mkt-page">
      <div className="t-eyebrow">about</div>
      <h1 className="mkt-page__h">Recognition is the skill nobody trains.</h1>
      <p className="mkt-page__lead">
        Most preparation evaluates final code. The hard part for competitors stuck below their
        ceiling isn&apos;t typing — it&apos;s seeing what a problem really is, fast, under pressure.
        usaCow trains exactly that, and it&apos;s free.
      </p>
      <p className="mkt-page__body">
        The product is deterministic by design: no chatbot grades you, no model invents a problem
        mid-session. Content is generated and verified offline, so every drill is fast, exact, and
        explainable.
      </p>
      <div className="mkt-page__cta">
        <Link to="/auth">
          <Button variant="primary" size="lg">
            Start free
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="mkt-page">
      <div className="t-eyebrow">privacy</div>
      <h1 className="mkt-page__h">Privacy policy</h1>
      <p className="mkt-page__lead">
        usaCow is built for students, so privacy is a product requirement, not an afterthought. This
        is a plain-language summary; it is not legal advice.
      </p>
      <ul className="mkt-policy">
        <li>
          <b>What we collect.</b> Your name and email from Google sign-in, and your learning
          activity (answers, progress, misconceptions) needed to teach you.
        </li>
        <li>
          <b>Age.</b> Accounts are for ages 13 and older. You attest to this at sign-up.
        </li>
        <li>
          <b>What we don&apos;t do.</b> We do not sell student data, run ads, or use your work for
          public marketing.
        </li>
        <li>
          <b>Your controls.</b> You can export everything we hold, or delete your account and all
          data, from Settings at any time.
        </li>
        <li>
          <b>Coaches and parents.</b> Sharing progress with a coach or parent is permissioned and
          shows reasoning growth, not raw submissions.
        </li>
      </ul>
      <div className="mkt-page__cta">
        <Link to="/auth">
          <Button variant="secondary" size="lg">
            Back to sign in
          </Button>
        </Link>
      </div>
    </div>
  );
}
